import { useEffect, useState, useCallback } from "react";
import "./AdminMusicalIntelligencePreview.css";
import "./AdminMusicalIntelligenceCanary.css";

const API = process.env.REACT_APP_BACKEND_URL;

const ALL_TRADITIONS = [
  "roman_catholic", "episcopal", "acna", "elca",
  "united_methodist", "presbyterian", "baptist", "generic",
];

const CLASSIFICATION_COLORS = {
  EXACT:              "#0e7a2c",
  FUNCTIONAL:         "#1e6091",
  ACCEPTABLE_ALT:     "#8a5a00",
  MATERIAL_DISAGREE:  "#a63232",
  INVALID:            "#a63232",
  MISSING:            "#666",
  FALLBACK:           "#a63232",
};

function TrafficLight({ passed }) {
  return (
    <span
      className={`mi-canary-tl ${passed ? "pass" : "fail"}`}
      data-testid={`canary-tl-${passed ? "pass" : "fail"}`}
    >
      {passed ? "PASS" : "FAIL"}
    </span>
  );
}

function VerdictBadge({ verdict }) {
  const cls = ({
    READY:              "verdict-ready",
    NOT_READY:          "verdict-not-ready",
    INSUFFICIENT_DATA:  "verdict-insufficient",
    ERROR:              "verdict-error",
  })[verdict] || "verdict-unknown";
  return (
    <span
      className={`mi-canary-verdict ${cls}`}
      data-testid={`canary-verdict-${verdict}`}
    >
      {verdict || "UNKNOWN"}
    </span>
  );
}


const LEDGER_STATE_META = {
  required:              { label: "Required",              included: true,  color: "#0e7a2c" },
  normative:             { label: "Normative",             included: true,  color: "#1e6091" },
  optional_selected:     { label: "Optional · Selected",   included: true,  color: "#5a4fcf" },
  conditional_active:    { label: "Conditional · Active",  included: true,  color: "#2f7d7b" },
  optional_omitted:      { label: "Optional · Omitted",    included: false, color: "#7a7a7a" },
  conditional_inactive:  { label: "Conditional · Inactive",included: false, color: "#8a5a00" },
  not_applicable:        { label: "Not Applicable",        included: false, color: "#a63232" },
};

const LEDGER_STATE_ORDER = [
  "required", "normative", "optional_selected", "conditional_active",
  "optional_omitted", "conditional_inactive", "not_applicable",
];


function LedgerPanel({ ledger, filters, setFilters, onExport }) {
  if (!ledger) return null;
  const inner = ledger.ledger || {};
  const rec = inner.reconciliation || {};
  const status = rec.status || ledger.reconciliation_status || "UNKNOWN";
  const entries = inner.entries || [];
  const byTradition = inner.by_tradition || {};
  const byProfile   = inner.by_profile   || {};
  const byScenario  = inner.by_scenario  || {};
  const byRole      = inner.by_role      || {};

  const filtered = entries.filter((e) => {
    if (filters.state !== "all" && e.state !== filters.state) return false;
    if (filters.tradition !== "all" && e.tradition !== filters.tradition) return false;
    if (filters.scenario !== "all" && e.scenario_id !== filters.scenario) return false;
    if (filters.role !== "all" && e.role_id !== filters.role) return false;
    if (filters.inclusion === "included" && !e.included_in_denominator) return false;
    if (filters.inclusion === "excluded" && e.included_in_denominator) return false;
    return true;
  });

  const statusClass = status === "PASS" ? "recon-pass" : "recon-fail";

  return (
    <section className="mi-canary-panel"
             data-testid="canary-slot-ledger-panel"
             style={{ borderTop: `4px solid ${status === "PASS" ? "#0e7a2c" : "#a63232"}` }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                    flexWrap: "wrap", gap: 12 }}>
        <h2 style={{ margin: 0 }}>Slot-Accounting Ledger (Phase 10.6.3)</h2>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span
            data-testid={`canary-ledger-status-${status}`}
            className={`mi-canary-verdict ${statusClass}`}
            style={{ background: status === "PASS" ? "#dff4e2" : "#f9dbdb",
                     color: status === "PASS" ? "#0e7a2c" : "#a63232",
                     padding: "4px 12px", borderRadius: 4, fontWeight: 700 }}>
            Reconciliation: {status}
          </span>
          <button className="mi-run"
                  data-testid="canary-ledger-export-json"
                  onClick={() => onExport("ledger.json")}>Ledger JSON</button>
          <button className="mi-run"
                  data-testid="canary-ledger-export-csv"
                  onClick={() => onExport("ledger.csv")}>Ledger CSV</button>
          <button className="mi-run"
                  data-testid="canary-ledger-export-md"
                  onClick={() => onExport("ledger.md")}>Ledger MD</button>
        </div>
      </div>

      {status !== "PASS" && (
        <div data-testid="canary-ledger-warning"
             style={{ marginTop: 12, padding: "10px 14px",
                      background: "#fbe9e7", border: "1px solid #a63232",
                      color: "#7a1a1a", borderRadius: 6 }}>
          ⚠ Reconciliation FAILED. Readiness verdict is degraded to NOT_READY
          regardless of underlying scores.{" "}
          {(rec.errors || []).join("; ")}
        </div>
      )}

      <div style={{ display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                    gap: 10, marginTop: 14 }}>
        <div><span>Total resolved opportunities</span>
             <strong data-testid="ledger-total-resolved">{rec.total_resolved ?? "—"}</strong></div>
        <div><span>Included denominator</span>
             <strong data-testid="ledger-included-denominator">{rec.included_denominator ?? "—"}</strong></div>
        <div><span>Excluded total</span>
             <strong data-testid="ledger-excluded-total">{rec.excluded_total ?? "—"}</strong></div>
        <div><span>Recommendation calls</span>
             <strong data-testid="ledger-rec-opps">{rec.recommendation_opportunities ?? "—"}</strong></div>
      </div>

      <details style={{ marginTop: 12 }}>
        <summary><strong>Identity equation</strong></summary>
        <pre data-testid="ledger-equation"
             style={{ background: "#f6f6f6", padding: 10, borderRadius: 4,
                      whiteSpace: "pre-wrap", fontSize: 12 }}>
{rec.equation}
{"\n"}{rec.proof}
        </pre>
      </details>

      <h3 style={{ marginTop: 16 }}>State breakdown</h3>
      <table className="mi-canary-table"
             data-testid="ledger-state-table">
        <thead>
          <tr><th>State</th><th>Count</th><th>Included?</th><th>Exclusion reason</th></tr>
        </thead>
        <tbody>
          {LEDGER_STATE_ORDER.map((s) => {
            const meta = LEDGER_STATE_META[s];
            const cnt = (rec.state_counts && rec.state_counts[s]) || 0;
            return (
              <tr key={s} data-testid={`ledger-state-row-${s}`}>
                <td>
                  <span style={{ display: "inline-block", width: 10, height: 10,
                                  background: meta.color, borderRadius: 2,
                                  marginRight: 8 }} />
                  {meta.label}
                </td>
                <td><strong>{cnt}</strong></td>
                <td>{meta.included ? "✅" : "—"}</td>
                <td style={{ fontSize: 12, color: "#666" }}>
                  {s === "not_applicable" && "Tradition does not use this role."}
                  {s === "conditional_inactive" && "Condition not met this service."}
                  {s === "optional_omitted" && "Parish intentionally omitted."}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <h3 style={{ marginTop: 16 }}>By tradition</h3>
      <table className="mi-canary-table" data-testid="ledger-by-tradition">
        <thead>
          <tr>
            <th>Tradition</th>
            {LEDGER_STATE_ORDER.map((s) => <th key={s}>{LEDGER_STATE_META[s].label}</th>)}
            <th>Included</th><th>Excluded</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(byTradition).sort().map(([trad, bkt]) => {
            const inc = ["required","normative","optional_selected","conditional_active"]
                        .reduce((s, k) => s + (bkt[k] || 0), 0);
            const exc = ["optional_omitted","conditional_inactive","not_applicable"]
                        .reduce((s, k) => s + (bkt[k] || 0), 0);
            return (
              <tr key={trad} data-testid={`ledger-trad-${trad}`}>
                <td>{trad}</td>
                {LEDGER_STATE_ORDER.map((s) =>
                  <td key={s}>{bkt[s] || 0}</td>
                )}
                <td><strong>{inc}</strong></td>
                <td>{exc}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <h3 style={{ marginTop: 16 }}>Entries (filterable)</h3>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
        <select data-testid="ledger-filter-state"
                value={filters.state}
                onChange={(e) => setFilters({ ...filters, state: e.target.value })}
                className="mi-canary-input">
          <option value="all">All states</option>
          {LEDGER_STATE_ORDER.map((s) =>
            <option key={s} value={s}>{LEDGER_STATE_META[s].label}</option>)}
        </select>
        <select data-testid="ledger-filter-tradition"
                value={filters.tradition}
                onChange={(e) => setFilters({ ...filters, tradition: e.target.value })}
                className="mi-canary-input">
          <option value="all">All traditions</option>
          {Object.keys(byTradition).sort().map((t) =>
            <option key={t} value={t}>{t}</option>)}
        </select>
        <select data-testid="ledger-filter-scenario"
                value={filters.scenario}
                onChange={(e) => setFilters({ ...filters, scenario: e.target.value })}
                className="mi-canary-input">
          <option value="all">All scenarios</option>
          {Object.keys(byScenario).sort().map((s) =>
            <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="ledger-filter-role"
                value={filters.role}
                onChange={(e) => setFilters({ ...filters, role: e.target.value })}
                className="mi-canary-input">
          <option value="all">All roles</option>
          {Object.keys(byRole).sort().map((r) =>
            <option key={r} value={r}>{r}</option>)}
        </select>
        <select data-testid="ledger-filter-inclusion"
                value={filters.inclusion}
                onChange={(e) => setFilters({ ...filters, inclusion: e.target.value })}
                className="mi-canary-input">
          <option value="all">Included + Excluded</option>
          <option value="included">Included only</option>
          <option value="excluded">Excluded only</option>
        </select>
        <div data-testid="ledger-entries-count" style={{ alignSelf: "center", color: "#666" }}>
          {filtered.length} / {entries.length} rows
        </div>
      </div>
      <div style={{ maxHeight: 400, overflowY: "auto", border: "1px solid #eee",
                    borderRadius: 4 }}>
        <table className="mi-canary-table small">
          <thead style={{ position: "sticky", top: 0, background: "#fafafa" }}>
            <tr>
              <th>Tradition</th><th>Profile</th><th>Scenario</th>
              <th>Role</th><th>Applicability</th><th>State</th>
              <th>Included</th><th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 500).map((e, i) => (
              <tr key={i} data-testid={`ledger-entry-row-${i}`}>
                <td>{e.tradition}</td>
                <td>{e.profile_id}</td>
                <td>{e.scenario_id}</td>
                <td>{e.role_id}</td>
                <td>{e.applicability_definition}</td>
                <td style={{ color: LEDGER_STATE_META[e.state]?.color }}>{e.state}</td>
                <td>{e.included_in_denominator ? "✅" : "—"}</td>
                <td style={{ fontSize: 11, color: "#666" }}>{e.exclusion_reason || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}


export default function AdminMusicalIntelligenceCanary() {
  const [runs, setRuns] = useState([]);
  const [activeRunId, setActiveRunId] = useState(null);
  const [activeRun, setActiveRun] = useState(null);
  const [reports, setReports] = useState([]);
  const [audit, setAudit] = useState([]);
  const [ledger, setLedger] = useState(null);
  const [ledgerFilters, setLedgerFilters] = useState({
    state: "all", tradition: "all", scenario: "all", role: "all",
    inclusion: "all",
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Form state
  const [selectedTraditions, setSelectedTraditions] = useState(ALL_TRADITIONS);
  const [reportsPerTradition, setReportsPerTradition] = useState(3);
  const [scenarioMode, setScenarioMode] = useState("standard_full_suite");

  // Signoff form
  const [signoffApproved, setSignoffApproved] = useState(false);
  const [signoffNotes, setSignoffNotes] = useState("");

  const loadRuns = useCallback(async () => {
    try {
      const r = await fetch(
        `${API}/api/admin/musical-intelligence/canary-runs?limit=20`,
        { credentials: "include" },
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setRuns(j.runs || []);
      if (!activeRunId && j.runs && j.runs.length) {
        setActiveRunId(j.runs[0].id);
      }
    } catch (e) { setErr(e.message); }
  }, [activeRunId]);

  const loadRun = useCallback(async (id) => {
    if (!id) return;
    try {
      const [runR, repR, auditR, ledgerR] = await Promise.all([
        fetch(`${API}/api/admin/musical-intelligence/canary-runs/${id}`,
              { credentials: "include" }),
        fetch(`${API}/api/admin/musical-intelligence/canary-runs/${id}/reports?limit=500`,
              { credentials: "include" }),
        fetch(`${API}/api/admin/musical-intelligence/canary-runs/${id}/audit`,
              { credentials: "include" }),
        fetch(`${API}/api/admin/musical-intelligence/canary-runs/${id}/ledger`,
              { credentials: "include" }),
      ]);
      if (runR.ok) setActiveRun(await runR.json());
      if (repR.ok) {
        const j = await repR.json();
        setReports(j.reports || []);
      }
      if (auditR.ok) {
        const j = await auditR.json();
        setAudit(j.audit_entries || []);
      }
      if (ledgerR.ok) {
        const j = await ledgerR.json();
        setLedger(j);
      } else {
        setLedger(null);
      }
    } catch (e) { setErr(e.message); }
  }, []);

  useEffect(() => { loadRuns(); }, [loadRuns]);
  useEffect(() => { loadRun(activeRunId); }, [activeRunId, loadRun]);

  // Poll every 2s while a run is not terminal.
  useEffect(() => {
    if (!activeRunId) return;
    const status = activeRun?.status;
    if (status && ["complete", "cancelled", "error"].includes(status)) return;
    const t = setInterval(() => loadRun(activeRunId), 2000);
    return () => clearInterval(t);
  }, [activeRunId, activeRun?.status, loadRun]);

  async function startRun() {
    setLoading(true);
    setErr("");
    try {
      const r = await fetch(
        `${API}/api/admin/musical-intelligence/canary-runs`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            traditions: selectedTraditions,
            reports_per_tradition: Number(reportsPerTradition) || 3,
            scenarios: scenarioMode,
          }),
        },
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setActiveRunId(j.run_id);
      await loadRuns();
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }

  async function cancelRun() {
    if (!activeRunId) return;
    await fetch(
      `${API}/api/admin/musical-intelligence/canary-runs/${activeRunId}/cancel`,
      { method: "POST", credentials: "include" },
    );
    loadRun(activeRunId);
  }

  async function rerunReadiness() {
    if (!activeRunId) return;
    await fetch(
      `${API}/api/admin/musical-intelligence/canary-runs/${activeRunId}/rerun-readiness`,
      { method: "POST", credentials: "include" },
    );
    loadRun(activeRunId);
  }

  async function submitSignoff() {
    if (!activeRunId) return;
    await fetch(
      `${API}/api/admin/musical-intelligence/canary-runs/${activeRunId}/owner-signoff`,
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ approved: signoffApproved, notes: signoffNotes }),
      },
    );
    loadRun(activeRunId);
  }

  function exportRun(format) {
    if (!activeRunId) return;
    const url = `${API}/api/admin/musical-intelligence/canary-runs/${activeRunId}/export?format=${format}`;
    window.open(url, "_blank");
  }

  function toggleTradition(t) {
    setSelectedTraditions((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t],
    );
  }

  // ---------------------------------------------------------------
  const progress = activeRun?.progress || {};
  const agg = activeRun?.aggregate || {};
  const vr  = activeRun?.readiness_verdict || {};
  const val = activeRun?.validation_report || {};
  const dims = val.dimensions || {};

  return (
    <div className="mi-canary-page" data-testid="canary-page">
      <header className="mi-page-head">
        <div>
          <div style={{ fontSize: 11, textTransform: "uppercase",
                         color: "#7a7a7a", letterSpacing: "0.06em",
                         marginBottom: 4 }}>
            Musical Intelligence™ · Internal
          </div>
          <h1 style={{ margin: 0, fontSize: 26, color: "#22343f" }}>
            Automatic Canary Validation
          </h1>
          <p style={{ margin: "6px 0 0", color: "#5a5a5a", fontSize: 13 }}>
            Owner-only. Seeded parishes. No LLM. No LIVE flip. Zero customer impact.
          </p>
        </div>
        <div className="mi-canary-actions">
          <a href="/admin/musical-intelligence/drift"
              className="mi-run"
              data-testid="link-to-drift-panel"
              style={{ textDecoration: "none", display: "inline-block",
                       background: "#22343f", marginRight: 8 }}>
            Denominator Drift Watcher →
          </a>
          <button
            className="mi-run"
            data-testid="canary-run-btn"
            onClick={startRun}
            disabled={loading || selectedTraditions.length === 0}
          >
            {loading ? "Starting…" : "Run Canary Validation"}
          </button>
        </div>
      </header>

      {err && <div className="mi-error" data-testid="canary-error">{err}</div>}

      <section className="mi-canary-config" data-testid="canary-config">
        <h2>Configuration</h2>
        <div className="mi-canary-cfg-row">
          <div>
            <label className="mi-canary-label">Traditions</label>
            <div className="mi-canary-chips">
              {ALL_TRADITIONS.map((t) => (
                <button
                  key={t}
                  data-testid={`canary-tradition-${t}`}
                  className={`mi-canary-chip ${selectedTraditions.includes(t) ? "active" : ""}`}
                  onClick={() => toggleTradition(t)}
                >
                  {t.replace(/_/g, " ")}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="mi-canary-label">Scenarios</label>
            <select
              data-testid="canary-scenario-mode"
              value={scenarioMode}
              onChange={(e) => setScenarioMode(e.target.value)}
              className="mi-canary-input"
            >
              <option value="standard_full_suite">Standard Full Suite (all applicable)</option>
            </select>
          </div>
          <div>
            <label className="mi-canary-label">Reports / tradition</label>
            <input
              data-testid="canary-reports-per-tradition"
              type="number"
              min={1} max={10}
              value={reportsPerTradition}
              onChange={(e) => setReportsPerTradition(e.target.value)}
              className="mi-canary-input"
            />
          </div>
        </div>
      </section>

      {activeRun && (
        <section className="mi-canary-status" data-testid="canary-status">
          <div className="mi-canary-status-row">
            <div>
              <div className="mi-canary-label">Run</div>
              <code data-testid="canary-run-id">{activeRun.id}</code>
            </div>
            <div>
              <div className="mi-canary-label">Status</div>
              <span
                data-testid="canary-status-value"
                className={`mi-canary-status-tag ${activeRun.status}`}
              >{activeRun.status}</span>
            </div>
            <div>
              <div className="mi-canary-label">Progress</div>
              <span data-testid="canary-progress">
                {progress.completed || 0} / {progress.total || 0}
                {progress.current_tradition &&
                  ` · ${progress.current_tradition} · ${progress.current_scenario}`}
              </span>
            </div>
            <div>
              <div className="mi-canary-label">Verdict</div>
              <VerdictBadge verdict={vr.verdict} />
            </div>
            <div className="mi-canary-status-actions">
              {["running", "queued"].includes(activeRun.status) &&
                <button className="mi-run"
                        data-testid="canary-cancel-btn"
                        onClick={cancelRun}>Cancel</button>}
              {activeRun.status === "complete" && (
                <>
                  <button className="mi-run"
                          data-testid="canary-rerun-readiness-btn"
                          onClick={rerunReadiness}>Re-run Readiness</button>
                  <button className="mi-run"
                          data-testid="canary-export-json"
                          onClick={() => exportRun("json")}>Export JSON</button>
                  <button className="mi-run"
                          data-testid="canary-export-csv"
                          onClick={() => exportRun("csv")}>Export CSV</button>
                  <button className="mi-run"
                          data-testid="canary-export-pdf"
                          onClick={() => exportRun("pdf")}>Export PDF</button>
                </>
              )}
            </div>
          </div>
        </section>
      )}

      {activeRun && activeRun.status === "complete" && (
        <>
          <LedgerPanel ledger={ledger}
                        filters={ledgerFilters}
                        setFilters={setLedgerFilters}
                        onExport={exportRun} />

          <section className="mi-canary-panel" data-testid="canary-aggregate">
            <h2>Aggregate Metrics</h2>
            <div className="mi-canary-grid">
              <div><span>Reports</span><strong>{agg.reports_generated ?? "—"}</strong></div>
              <div><span>Traditions</span><strong>{(agg.traditions_covered || []).length}</strong></div>
              <div><span>Scenarios</span><strong>{(agg.scenarios_covered || []).length}</strong></div>
              <div><span>Agreement rate</span><strong>{((agg.agreement_rate || 0) * 100).toFixed(1)}%</strong></div>
              <div><span>Fallback reports</span><strong>{agg.fallback_reports ?? "—"}</strong></div>
              <div><span>Error reports</span><strong>{agg.error_reports ?? "—"}</strong></div>
              <div><span>p50 latency</span><strong>{agg.p50_latency_ms ?? "—"} ms</strong></div>
              <div><span>p95 latency</span><strong>{agg.p95_latency_ms ?? "—"} ms</strong></div>
              <div><span>Grade</span><strong>{val.grade || "—"}</strong></div>
              <div><span>Readiness Score</span><strong>{val.readiness_score ?? "—"}</strong></div>
            </div>
            <details className="mi-canary-classifications">
              <summary>Classification breakdown</summary>
              <div className="mi-canary-class-grid">
                {Object.entries(agg.classification_counts || {}).map(([k, v]) => (
                  <div key={k}
                       data-testid={`canary-class-${k}`}
                       style={{ borderLeft: `4px solid ${CLASSIFICATION_COLORS[k] || "#999"}` }}>
                    <span>{k}</span><strong>{v}</strong>
                  </div>
                ))}
              </div>
            </details>
          </section>

          <section className="mi-canary-panel" data-testid="canary-dimensions">
            <h2>8-Dimension Validation Matrix</h2>
            <div className="mi-canary-dims-grid">
              {[
                ["recommendation_accuracy",   "Recommendation Accuracy"],
                ["liturgical_appropriateness","Liturgical Appropriateness"],
                ["seasonal_consistency",      "Seasonal Consistency"],
                ["denominational_correctness","Denominational Correctness"],
                ["hymnal_reference_accuracy", "Hymnal Reference Accuracy"],
                ["performance_latency",       "Performance / Latency"],
                ["fallback_frequency",        "Fallback Frequency"],
                ["agreement_disagreement",    "Agreement / Disagreement"],
              ].map(([k, label]) => {
                const d = dims[k] || {};
                return (
                  <div key={k} className="mi-canary-dim"
                       data-testid={`canary-dim-${k}`}>
                    <div className="mi-canary-dim-label">{label}</div>
                    <div className="mi-canary-dim-score">
                      {d.score !== null && d.score !== undefined
                        ? `${(d.score * 100).toFixed(1)}%`
                        : "—"}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="mi-canary-panel" data-testid="canary-gates">
            <h2>LIVE Readiness Gates</h2>
            <table className="mi-canary-gates-table">
              <thead>
                <tr><th>Gate</th><th>Required</th><th>Actual</th><th>Status</th></tr>
              </thead>
              <tbody>
                {(vr.checks || []).map((c) => (
                  <tr key={c.name} data-testid={`canary-gate-${c.name}`}>
                    <td>{c.name}</td>
                    <td>{String(c.required)}</td>
                    <td>{String(c.actual)}</td>
                    <td><TrafficLight passed={!!c.passed} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="mi-canary-panel" data-testid="canary-tradition-coverage">
            <h2>Tradition Coverage</h2>
            <table className="mi-canary-table">
              <thead>
                <tr><th>Tradition</th><th>Reports</th><th>Fallbacks</th></tr>
              </thead>
              <tbody>
                {ALL_TRADITIONS.map((t) => {
                  const rs = reports.filter((r) => r.tradition === t);
                  const fb = rs.filter((r) => r.fallback_used).length;
                  if (rs.length === 0) return null;
                  return (
                    <tr key={t} data-testid={`canary-trad-row-${t}`}>
                      <td>{t}</td>
                      <td>{rs.length}</td>
                      <td>{fb}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>

          <section className="mi-canary-panel" data-testid="canary-comparison-viewer">
            <h2>Recommendation Comparisons</h2>
            <div className="mi-canary-comp-list">
              {reports.slice(0, 6).map((r) => (
                <div key={r.id} className="mi-canary-comp-block"
                     data-testid={`canary-comp-${r.id}`}>
                  <div className="mi-canary-comp-title">
                    {r.tradition} · {r.scenario_id} · <em>{r.profile_id}</em>
                    {r.fallback_used && <span className="mi-canary-badge fallback">Fallback</span>}
                  </div>
                  <table className="mi-canary-table small">
                    <thead>
                      <tr><th>Slot</th><th>Legacy</th><th>Engine</th><th>Class</th></tr>
                    </thead>
                    <tbody>
                      {(r.comparisons || []).map((c) => (
                        <tr key={c.slot}>
                          <td>{c.slot}</td>
                          <td>{c.legacy_pick?.title || "—"}</td>
                          <td>{c.engine_pick?.title || "—"}
                              {c.engine_pick?.tier && ` · T${c.engine_pick.tier}`}</td>
                          <td>
                            <span className="mi-canary-class"
                                  style={{ color: CLASSIFICATION_COLORS[c.classification] }}>
                              {c.classification}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </section>

          <section className="mi-canary-panel" data-testid="canary-audit-panel">
            <h2>Recommendation Audit ({audit.length})</h2>
            <div className="mi-canary-audit-list">
              {audit.slice(0, 8).map((a, i) => (
                <div key={i} className="mi-canary-audit-item"
                     data-testid={`canary-audit-${i}`}>
                  <div className="mi-canary-audit-header">
                    <strong>{a.selected_work?.title || "—"}</strong>
                    <span>{a.slot} · {a.tradition} · {a.scenario_id}</span>
                    <span className={`mi-canary-ref ${a.reference_status}`}>
                      {a.reference_status}
                    </span>
                  </div>
                  <div className="mi-canary-audit-rationale">{a.rationale}</div>
                  <div className="mi-canary-audit-codes">
                    {(a.reasoning_codes || []).map((c) =>
                      <span key={c} className="mi-canary-code">{c}</span>)}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mi-canary-panel" data-testid="canary-signoff">
            <h2>Owner Signoff</h2>
            <p className="mi-canary-hint">
              Owner signoff never changes the integration mode automatically.
              After signoff, flipping LIVE remains a manual production deploy step.
            </p>
            <div className="mi-canary-signoff-row">
              <label>
                <input
                  data-testid="canary-signoff-approved"
                  type="checkbox"
                  checked={signoffApproved}
                  onChange={(e) => setSignoffApproved(e.target.checked)}
                />
                Approved for LIVE
              </label>
              <textarea
                data-testid="canary-signoff-notes"
                placeholder="Notes for the sign-off log…"
                value={signoffNotes}
                onChange={(e) => setSignoffNotes(e.target.value)}
                rows={3}
                className="mi-canary-input"
              />
              <button
                data-testid="canary-signoff-submit"
                className="mi-run"
                onClick={submitSignoff}
              >Record signoff</button>
            </div>
            {activeRun.owner_signoff && (
              <div className="mi-canary-signoff-current"
                   data-testid="canary-signoff-current">
                Current: {activeRun.owner_signoff.approved ? "APPROVED" : "NOT APPROVED"}
                {" · "}by {activeRun.owner_signoff.by}
                {" · "}{activeRun.owner_signoff.at}
                {activeRun.owner_signoff.notes && (
                  <div>Notes: {activeRun.owner_signoff.notes}</div>
                )}
              </div>
            )}
          </section>
        </>
      )}

      <section className="mi-canary-panel" data-testid="canary-history">
        <h2>Recent Runs</h2>
        <table className="mi-canary-table">
          <thead>
            <tr><th>ID</th><th>Started</th><th>Status</th><th>Verdict</th><th>Traditions</th></tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id}
                  data-testid={`canary-history-${r.id}`}
                  className={r.id === activeRunId ? "active" : ""}
                  onClick={() => setActiveRunId(r.id)}
                  style={{ cursor: "pointer" }}>
                <td><code>{r.id}</code></td>
                <td>{r.started_at?.slice(0, 19).replace("T", " ")}</td>
                <td>{r.status}</td>
                <td>{r.readiness_verdict?.verdict || "—"}</td>
                <td>{(r.traditions || []).length}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
